import { IS_IOS, IS_WEB } from 'constants/platform'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { ActionIcon } from 'partials/PostCard/subcomponents'
import { WorkoutTagsModal } from 'partials/WorkoutTags'
import { HorizontalTagsLayout } from 'partials/WorkoutTags/subcomponents/HorizontalTagsLayout'

import { Box } from 'widgets/Box'
import { BorderlessField } from 'widgets/Field'
import { Icon } from 'widgets/Icon'
import { Pressable } from 'widgets/Pressable'
import { Separator } from 'widgets/Separator'
import { Text } from 'widgets/Text'

import { useCurrentUser } from 'hooks/useCurrentUser'
import { useGroup } from 'hooks/useGroup'
import { usePrograms } from 'hooks/usePrograms'

import commonStyles from 'utils/styles/commonStyles'

import { useTextEditorModal } from 'contexts/TextEditorModalContext'

import { InProgressPost } from 'types/post'
import { TagGroup } from 'types/workoutTag'

import { BaseStepForm } from './BaseStepForm'

interface Props {
  initialValues: InProgressPost
  onValuesChange: (values: Partial<InProgressPost>) => void
}

export const WorkoutDetailsForm = ({ initialValues, onValuesChange }: Props) => {
  const [isTagsModalVisible, setIsTagsModalVisible] = useState<boolean>(false)
  const [workoutTypes, setWorkoutTypes] = useState<TagGroup | undefined>(undefined)
  const [muscleGroup, setMuscleGroup] = useState<TagGroup | undefined>(undefined)
  const [durations, setDurations] = useState<TagGroup | undefined>(undefined)
  const [locations, setLocations] = useState<TagGroup | undefined>(undefined)
  const [combinedTagsData, setCombinedTagsData] = useState<TagGroup | undefined>()

  const textEditorModalRef = useTextEditorModal()
  const { data: currentUser } = useCurrentUser()
  const { data: group, meta } = useGroup({ userId: currentUser?.id })
  const { hasSetupGroup } = meta

  const { data: programs } = usePrograms(currentUser?.id)

  const [viewerCategories, setViewerCategories] = useState<{ value: string; label: string }[]>([
    {
      label: 'Draft',
      value: 'draft',
    },
    {
      label: 'Friends',
      value: 'friends',
    },
    {
      label: 'Followers',
      value: 'followers',
    },
  ])

  useEffect(() => {
    if (!hasSetupGroup) return
    setViewerCategories([
      ...viewerCategories,
      {
        label: group?.title ?? 'Your Group',
        value: 'upspace',
      },
    ])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSetupGroup])

  useEffect(() => {
    if (
      !workoutTypes?.tags.length &&
      !muscleGroup?.tags.length &&
      !durations?.tags.length &&
      !locations?.tags.length
    ) {
      setCombinedTagsData(undefined)
      return
    }

    setCombinedTagsData({
      id: '',
      name: '',
      style: 'badge',
      maxSelection: workoutTypes?.maxSelection,
      tags: [
        ...(workoutTypes?.tags ?? []),
        ...(muscleGroup?.tags ?? []),
        ...(durations?.tags.map((item) => `${item} min`) ?? []),
        ...(locations?.tags ?? []),
      ],
    })
  }, [workoutTypes, muscleGroup, durations, locations])

  useEffect(() => {
    setupGroupTags()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    initialValues.tagGroups = {
      workoutTypes: workoutTypes?.tags ?? [],
      muscleGroup: muscleGroup?.tags ?? [],
      duration: durations?.tags ?? [],
      location: locations?.tags ?? [],
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workoutTypes, muscleGroup, durations, locations])

  const setupGroupTags = () => {
    const workoutTypesTags = createEmptyTagGroup()
    workoutTypesTags.tags = initialValues.tagGroups?.workoutTypes ?? []
    setWorkoutTypes(workoutTypesTags)

    const muscleGroupTags = createEmptyTagGroup()
    muscleGroupTags.tags = initialValues.tagGroups?.muscleGroup ?? []
    setMuscleGroup(muscleGroupTags)

    const durationsTags = createEmptyTagGroup()
    durationsTags.tags = initialValues.tagGroups?.duration ?? []
    durationsTags.style = 'circle'
    setDurations(durationsTags)

    const locationsTags = createEmptyTagGroup()
    locationsTags.tags = initialValues.tagGroups?.location ?? []
    setLocations(locationsTags)
  }

  const createEmptyTagGroup = (): TagGroup => {
    return {
      id: '',
      name: '',
      maxSelection: undefined,
      style: 'badge',
      tags: [],
    }
  }

  const presentTextEditor = () => {
    textEditorModalRef?.current?.present({
      title: 'Workout Summary',
      inputText: initialValues.description,
      onSubmit: (inputText: string) => {
        onValuesChange({
          ...initialValues,
          description: inputText?.trim() ?? '',
        })
      },
    })
  }

  const memoizedProgramIds = useMemo((): { value: string; label: string }[] => {
    if (!programs) return []

    const programIdsList: { label: string; value: string }[] = []
    programs.forEach((program, index) => {
      if (!program) return
      programIdsList.push({
        label: programs[index]?.title ?? 'Untitled Program',
        value: program.id,
      })
    })

    return programIdsList
  }, [programs])

  const getChildren = useCallback(
    (mediaSelector: JSX.Element) => (
      <>
        <Box zIndex={99} flexDirection='row' margin={{ top: 'md' }}>
          <Box flex={1} justifyContent='center' align='center'>
            {mediaSelector}
          </Box>

          <Box flex={2}>
            <Box flex={1}>
              <BorderlessField required name='title' type='text' placeholder='Enter title' />
            </Box>

            <Box
              zIndex={100}
              margin={{ left: 'xs' }}
              padding={{ left: 'lg', right: 'lg' }}
              style={IS_IOS ? { marginTop: -4 } : undefined}
            >
              <Separator />
            </Box>

            <Box
              flex={1}
              margin={{ left: 'xs' }}
              zIndex={99}
              style={IS_IOS ? { marginTop: -4 } : undefined}
            >
              <BorderlessField
                multiSelect
                type='select'
                options={viewerCategories}
                isSearchable={false}
                placeholder='Choose Groups'
                name='combinedCategoriesAndProgramIds'
              />
            </Box>

            <Box zIndex={99} margin={{ left: 'xs' }} padding={{ left: 'lg', right: 'lg' }}>
              <Separator />
            </Box>

            <Box
              flex={1}
              margin={{ left: 'xs' }}
              zIndex={98}
              style={IS_IOS ? { marginTop: -4 } : undefined}
            >
              <BorderlessField
                multiSelect
                type='select'
                options={memoizedProgramIds}
                isSearchable={false}
                placeholder='Choose Programs'
                name='publishedToProgramIds'
              />
            </Box>

            <Box margin={{ left: 'xs' }} padding={{ left: 'lg', right: 'lg' }}>
              <Separator />
            </Box>
          </Box>
        </Box>

        <Box
          height={50}
          width='100%'
          justifyContent='center'
          padding={{ right: 'smd' }}
          margin={{ top: 'sm', left: 'xs' }}
        >
          {combinedTagsData?.tags?.length ? (
            <Box flexDirection='row' align='center'>
              <Box
                flex={1}
                flexDirection={IS_WEB ? 'column' : 'row'}
                padding={{ right: 'xs' }}
                style={{ marginBottom: -8 }}
              >
                <HorizontalTagsLayout hideTitle data={combinedTagsData} />
              </Box>
              <ActionIcon
                iconSize={22}
                color='greyDarker'
                icon='chevron-right'
                action={() => setIsTagsModalVisible(true)}
              />
            </Box>
          ) : (
            <Pressable onPress={() => setIsTagsModalVisible(true)}>
              <Box flexDirection='row' align='center' justifyContent='space-between'>
                <Text fontSize='sm' color='greyDarker'>
                  Discovery Tags
                </Text>
                <Icon color='greyDarker' size={20} name='chevron-right' />
              </Box>
            </Pressable>
          )}
        </Box>

        <Box margin={{ top: 'smd' }} padding={{ horizontal: 'lg' }}>
          <Separator />
        </Box>

        <Box width='100%' margin={{ top: 'smd', horizontal: 'xs' }} flexDirection='row'>
          <Pressable onPress={presentTextEditor} style={commonStyles.fullSize}>
            <Text
              fontSize='sm'
              color={initialValues?.description?.length ? 'greyDarkest' : 'greyDark'}
            >
              {initialValues?.description?.length
                ? initialValues?.description
                : 'Workout Summary...'}
            </Text>
          </Pressable>
        </Box>
      </>
    ),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [combinedTagsData, durations, locations, initialValues],
  )

  return (
    <Box zIndex={99}>
      <BaseStepForm<InProgressPost>
        allowedFileType='image'
        onValuesChange={onValuesChange}
        initialValues={{ ...initialValues }}
      >
        {getChildren}
      </BaseStepForm>
      <WorkoutTagsModal
        isVisible={isTagsModalVisible}
        onClose={(selection: any) => {
          setIsTagsModalVisible(false)
          setWorkoutTypes(selection?.workoutTypes)
          setMuscleGroup(selection?.muscleGroup)
          setDurations(selection?.duration)
          setLocations(selection?.location)
        }}
      />
    </Box>
  )
}
